import React from 'react'
import styled from 'styled-components'
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  fadeInUp,
  fadeInLeft,
  staggerContainer,
  smoothEase,
} from './animations/AnimatedSection'

const cardPopVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: smoothEase },
  },
}

const ScienceCommunitySection = ({ id }: { id?: string }) => {
  const cards = [
    {
      title: 'Universities & Researchers',
      description:
        'Access our research repository, contribute findings, and translate your work into actionable community engagement.',
    },
    {
      title: 'Government & Policy',
      description:
        'Leverage the CoGs storytelling framework to build public legitimacy for evidence-based SDG policy decisions.',
    },
    {
      title: 'Science Institutions',
      description:
        'Partner with us to create action infrastructure that bridges the gap between scientific reporting and public engagement.',
    },
    {
      title: 'Educators & Schools',
      description:
        'Use our interactive superhero framework and SDG curriculum to engage the next generation of global stewards.',
    },
  ]

  return (
    <Section id={id}>
      <Container>
        <LeftColumn>
          <AnimatedSection variants={fadeInLeft}>
            <SectionLabel>FOR THE SCIENCE COMMUNITY</SectionLabel>
          </AnimatedSection>
          <AnimatedSection variants={fadeInLeft} delay={0.1}>
            <Title>
              Action Infrastructure for <HighlightSpan>Impact</HighlightSpan>
            </Title>
          </AnimatedSection>
          <AnimatedSection variants={fadeInLeft} delay={0.2}>
            <Description>
              Despite decades of scientific reporting on climate change,
              biodiversity loss, and systemic instability, decision-making
              remains fragmented. CoGs reframes storytelling as action
              infrastructure — a system that translates risk into roles, enables
              participation, and generates momentum for coordinated response.
            </Description>
          </AnimatedSection>
          <AnimatedSection variants={fadeInUp} delay={0.3}>
            <CTAButton>Explore Our Research Feature</CTAButton>
          </AnimatedSection>
        </LeftColumn>

        <RightColumn>
          <StaggerContainer
            variants={staggerContainer}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '32px',
              width: '100%',
            }}
          >
            {cards.map((card, idx) => (
              <StaggerItem key={idx} variants={cardPopVariants}>
                <CommunityCard>
                  <CardTitle>{card.title}</CardTitle>
                  <CardDescription>{card.description}</CardDescription>
                </CommunityCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </RightColumn>
      </Container>
    </Section>
  )
}

const Section = styled.section`
  width: 100%;
  background: radial-gradient(
    circle at 133% -101%,
    #50a94b 0%,
    #2b6328 42%,
    #051d05 75%
  );
  padding: 120px 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 80px 24px;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 64px;
  max-width: 1283px;
  width: 100%;

  @media (max-width: 1100px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 48px;
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  max-width: 540px;
  width: 100%;
`

const SectionLabel = styled.span`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  color: #bbe148;
`

const Title = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 48px;
  line-height: 56px;
  letter-spacing: -0.04em;
  color: #ffffff;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }
`

const HighlightSpan = styled.span`
  background: linear-gradient(90deg, #0e6409 0%, #bbe148 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`

const Description = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
`

const CTAButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 14px 32px;
  width: fit-content;
  min-height: 52px;
  background: #0e6409;
  border: 1px solid #2a2a2a;
  border-radius: 100px;
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  letter-spacing: 0px;

  &:hover {
    background: #0a4a06;
    transform: translateY(-2px);
  }
`

const RightColumn = styled.div`
  flex: 1;
  width: 100%;

  @media (max-width: 650px) {
    & > div {
      grid-template-columns: 1fr !important;
    }
  }
`

const CommunityCard = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 32px;
  gap: 16px;
  width: 100%;
  min-height: 236px;
  background: rgba(255, 255, 255, 0.05);
  border-width: 1px 4px 4px 1px;
  border-style: solid;
  border-color: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  transition:
    transform 0.3s ease,
    background 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 1200px) {
    width: 100%;
    height: auto;
    min-height: 200px;
  }
`

const CardTitle = styled.h4`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  color: #ffffff;
  margin: 0;
`

const CardDescription = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }
`

export default ScienceCommunitySection
